import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

/** `std-state-dot` — the running mark, in four states.
 *
 * This replaces the spinner. A spinner is a rotating GLYPH, and a rotating
 * glyph is the one shape a transcript cannot afford: it is a different optical
 * weight at every frame, so a column of rows containing one has no stable left
 * edge, and it says "busy" without saying anything about what settled.
 *
 * The shape is adopted from dsh's StateDot, and its whole idea is that the
 * running mark and the settled mark are ONE object in one box. A reader
 * watching a row does not see a spinner disappear and a circle appear; they
 * see the same 14px cell stop moving and take a colour. That is why this is a
 * component with a `state` prop rather than two components a caller swaps
 * between.
 *
 *   ongoing — a 3x3 pixel matrix whose 8 outer cells light clockwise
 *   done    — a green core
 *   error   — a red core
 *   warn    — a yellow core
 *
 * ## Why a pixel matrix rather than an arc
 *
 * An arc sweeping a circle is a spinner with the glyph filed off: it is drawn
 * with subpixel geometry, so at 14px it is a grey smear that shimmers as it
 * turns. A 3x3 matrix of whole pixels has no subpixel geometry to smear —
 * `shape-rendering: crispEdges` pins every cell to the device grid — and the
 * motion is carried by OPACITY on cells that never move. Nothing on the row
 * changes size, position or weight while it runs, which is the property a
 * dense transcript needs and the property a spinner cannot have.
 *
 * ## Why the trail is stepped and not tweened
 *
 * The four trail values (1, 0.6, 0.35, 0.15) are held FLAT across their whole
 * step — `step-end` on the animation, so each keyframe's value stands until
 * the next one rather than ramping into it. A tweened trail reads as a
 * gradient rotating, which is the smear again; a stepped one reads as a
 * comet's head jumping cell to cell, which is what the pixel grid is for.
 *
 * Every cell runs the SAME 1s animation and differs only in phase:
 * `animation-delay: (i - 8) * 125ms`, negative so that the cycle is already
 * underway at mount and the mark is never a blank box for its first frame.
 * The delays run against the DOM order of the rects, which is why those are
 * authored clockwise from the top-left and must stay that way.
 *
 * ## The dot has no name
 *
 * It is `aria-hidden`, always. A status mark beside a row that already says
 * "Ran cargo build · exit 0" is a second voice reading the same fact, and
 * three of the four states have no wording of their own that a caller has not
 * already written. Callers own the accessible label; this owns the ink. */
@component({ name: "std-state-dot" })
export class StateDot extends LonaWebComponent {
  static makeWith(props: StateDot.Props): StateDot {
    return (StateDot.make() as StateDot).bind(props);
  }

  /** Idempotent by construction: every field this component has is the one
   * attribute, and it is SET rather than toggled, so a pooled instance
   * arriving from a `done` row renders `ongoing` with nothing left over. */
  bind(props: StateDot.Props): this {
    this.setAttribute("state", props.state);
    this.setAttribute("aria-hidden", "true");
    return this;
  }

  static $styles = [
    css`
      /* 14px is not a taste value: the matrix is 3 cells of 4px separated by
         1px gutters, so the box is 4+1+4+1+4 and the viewBox maps 1:1 to
         device pixels at this size. --state-dot-size exists for a caller that
         must match a neighbouring column, and a caller that changes it is
         choosing to let crispEdges round the cells unevenly. */
      :host {
        display: inline-flex;
        flex: none;
        width: var(--state-dot-size, 14px);
        height: var(--state-dot-size, 14px);
        /* Through --fg-*, NOT the raw palette hues: the theme's --blue,
           --green and --red are SYSTEM colours picked for a dark surface, and
           the --fg-* twins are mixed toward --text-pri so they stay legible on
           either. Measured on the dark theme, --fg-blue/--fg-red/--fg-green
           resolve and --fg-yellow does NOT, so the warn state writes the mix out by
           hand as its fallback — an undefined custom property falls to the
           literal here, not to a related token, and a flat #d4a72c on a dark
           panel is the bug std-exception's diagnostic code shipped. */
        --state-dot-tone: var(--text-ter, #8b93a7);
      }
      :host([state="ongoing"]) {
        --state-dot-tone: var(--fg-blue, #3b82f6);
      }
      :host([state="done"]) {
        --state-dot-tone: var(--fg-green, #2da44e);
      }
      :host([state="error"]) {
        --state-dot-tone: var(--fg-red, #e5484d);
      }
      :host([state="warn"]) {
        --state-dot-tone: var(
          --fg-yellow,
          color-mix(in srgb, var(--yellow, #d4a72c) 72%, var(--text-pri, #1f2328) 28%)
        );
      }

      #svg {
        display: block;
        width: 100%;
        height: 100%;
        overflow: visible;
      }

      /* Both faces are always in the tree and exactly one is drawn. Rendering
         the other face means REPLACING nodes, which is a second code path that
         can disagree with the first about geometry; this way the two faces
         cannot drift out of the same box. */
      #matrix,
      #core {
        display: none;
      }
      :host([state="ongoing"]) #matrix {
        display: block;
      }
      :host([state="done"]) #core,
      :host([state="error"]) #core,
      :host([state="warn"]) #core {
        display: block;
      }

      /* ---- ongoing: the pixel matrix ------------------------------- */
      #matrix rect {
        fill: var(--state-dot-tone);
        /* Dark by default so a cell the animation has not reached yet is
           absent rather than a faint ghost — the trail is the only thing that
           should be visible in this box. */
        opacity: 0;
        shape-rendering: crispEdges;
      }
      :host([state="ongoing"]) #matrix rect {
        /* step-end is what makes the trail four HOLDS instead of a ramp: each
           keyframe's opacity stands until the next keyframe rather than
           interpolating into it. */
        animation: std-state-dot-trail 1s step-end infinite;
      }
      /* (i - 8) * 125ms. Negative, so every cell is already mid-cycle at
         mount; the order of these selectors is the clockwise order the rects
         are authored in. */
      #matrix rect:nth-of-type(1) { animation-delay: -1000ms; }
      #matrix rect:nth-of-type(2) { animation-delay: -875ms; }
      #matrix rect:nth-of-type(3) { animation-delay: -750ms; }
      #matrix rect:nth-of-type(4) { animation-delay: -625ms; }
      #matrix rect:nth-of-type(5) { animation-delay: -500ms; }
      #matrix rect:nth-of-type(6) { animation-delay: -375ms; }
      #matrix rect:nth-of-type(7) { animation-delay: -250ms; }
      #matrix rect:nth-of-type(8) { animation-delay: -125ms; }

      /* Head, then a three-step trail, then dark for the second half of the
         cycle. Four lit cells out of eight is what makes the direction
         readable — a trail longer than half the ring reads as a pulse. */
      @keyframes std-state-dot-trail {
        0% { opacity: 1; }
        12.5% { opacity: 0.6; }
        25% { opacity: 0.35; }
        37.5% { opacity: 0.15; }
        50% { opacity: 0; }
        100% { opacity: 0; }
      }

      /* ---- settled: a core and its halo ---------------------------- */
      /* The halo is the whole reason a settled dot reads at 6px. A bare 6px
         disc on a text row is a speck; the same disc inside a 10px wash of its
         own colour at 10% has a presence the row's words do not have to
         compete with. Circles, so shape-rendering stays off them — crispEdges
         on a curve is a staircase. */
      #halo {
        fill: var(--state-dot-tone);
        opacity: 0.1;
      }
      #dot {
        fill: var(--state-dot-tone);
      }

      /* The motion IS the message here, so what replaces it has to carry the
         same fact: this is the animation's first frame, frozen — the head cell
         and its three-step trail, held. An animation:none with nothing else
         said would have left every cell at its 0 default and shipped an empty
         box where the running mark should be. */
      @media (prefers-reduced-motion: reduce) {
        :host([state="ongoing"]) #matrix rect {
          animation: none;
        }
        :host([state="ongoing"]) #matrix rect:nth-of-type(1) { opacity: 1; }
        :host([state="ongoing"]) #matrix rect:nth-of-type(8) { opacity: 0.6; }
        :host([state="ongoing"]) #matrix rect:nth-of-type(7) { opacity: 0.35; }
        :host([state="ongoing"]) #matrix rect:nth-of-type(6) { opacity: 0.15; }
      }
    `,
  ];

  /* The 8 outer cells, CLOCKWISE from the top-left — the delays above index
     this order, so reordering these rects silently reverses the spin. The
     centre cell is deliberately absent: a lit centre turns the ring into a
     blob at this size, and it carries no phase of its own. */
  static $html: Option<HTMLTemplateElement> = html`
    <svg id="svg" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
      <g id="matrix">
        <rect x="0" y="0" width="4" height="4"></rect>
        <rect x="5" y="0" width="4" height="4"></rect>
        <rect x="10" y="0" width="4" height="4"></rect>
        <rect x="10" y="5" width="4" height="4"></rect>
        <rect x="10" y="10" width="4" height="4"></rect>
        <rect x="5" y="10" width="4" height="4"></rect>
        <rect x="0" y="10" width="4" height="4"></rect>
        <rect x="0" y="5" width="4" height="4"></rect>
      </g>
      <g id="core">
        <circle id="halo" cx="7" cy="7" r="5"></circle>
        <circle id="dot" cx="7" cy="7" r="3"></circle>
      </g>
    </svg>
  `;
}

export namespace StateDot {
  /** Four, and deliberately not five. `ongoing` is every state before a
   * verdict — a call that has not started and a call that is running are the
   * same fact to a reader waiting on it — and the three settled states are the
   * three verdicts a surface can report. A skin that needs a fifth wording
   * says it in words beside the dot, not in a fifth colour. */
  export type State = "ongoing" | "done" | "error" | "warn";

  export type Props = { state: State };
}
